import { useState } from 'react';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { StudyPlanDraftPreview } from '../../types';

interface StudyPlanDraftCardProps {
  data: StudyPlanDraftPreview;
  onAccept?: () => void;
  onDismiss?: () => void;
  savedUrl?: string;
}

export function StudyPlanDraftCard({ data, onAccept, onDismiss, savedUrl }: StudyPlanDraftCardProps) {
  const [status, setStatus] = useState<'draft' | 'saved' | 'dismissed'>('draft');

  const handleAccept = () => {
    setStatus('saved');
    onAccept?.();
  };

  const handleDismiss = () => {
    setStatus('dismissed');
    onDismiss?.();
  };

  if (status === 'dismissed') return null;

  return (
    <Card>
      <CardHeader className="p-4 pb-2">
        <div className="flex items-center gap-2">
          <Badge variant="warning">Draft</Badge>
        </div>
        <CardTitle className="text-sm mt-2">{data.title}</CardTitle>
      </CardHeader>
      <CardContent className="p-4 pt-0 space-y-3">
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-1">Topics</p>
          <div className="flex flex-wrap gap-1">
            {data.topics.map((topic) => (
              <Badge key={topic} variant="secondary">{topic}</Badge>
            ))}
          </div>
        </div>
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-1">Schedule</p>
          <p className="text-sm whitespace-pre-line">{data.scheduleOutline}</p>
        </div>
      </CardContent>
      <CardFooter className="p-4 pt-0 gap-2">
        {status === 'draft' ? (
          <>
            <Button size="sm" onClick={handleAccept}>Accept</Button>
            <Button size="sm" variant="outline" onClick={handleDismiss}>Dismiss</Button>
          </>
        ) : (
          <div className="flex items-center gap-2 text-sm text-green-600">
            <span>Saved ✓</span>
            {savedUrl && (
              <a href={savedUrl} className="underline">View</a>
            )}
          </div>
        )}
      </CardFooter>
    </Card>
  );
}
